import { ref, onUnmounted, nextTick, watch, type Ref } from 'vue'

export interface UseInfiniteScrollOptions {
  rootMargin?: string
  threshold?: number
  enabled?: () => boolean
  debounceMs?: number
}

export interface UseInfiniteScrollReturn {
  loadMoreTrigger: Ref<HTMLElement | undefined>
  setupObserver: () => void
  cleanupObserver: () => void
  isObserverActive: Ref<boolean>
}

/**
 * Generic Infinite Scroll Composable with integrated IntersectionObserver
 * Handles intersection observer logic for infinite scrolling
 * Can be reused across different infinite list components
 */
export const useInfiniteScroll = (callback: () => void, options: UseInfiniteScrollOptions = {}): UseInfiniteScrollReturn => {
  const { rootMargin = '200px', threshold = 0.1, enabled = () => true, debounceMs = 50 } = options

  // Refs
  const loadMoreTrigger = ref<HTMLElement>()
  const isObserverActive = ref(false)
  const timeoutRef = ref<NodeJS.Timeout>()
  const observer = ref<IntersectionObserver>()

  /**
   * Debounced callback to prevent rapid firing
   */
  const debouncedCallback = () => {
    if (timeoutRef.value) {
      clearTimeout(timeoutRef.value)
    }

    timeoutRef.value = setTimeout(() => {
      if (enabled()) {
        callback()
      }
    }, debounceMs)
  }

  /**
   * Setup intersection observer for the trigger element
   */
  const setupIntersectionObserver = (
    trigger: Ref<HTMLElement | undefined>,
    observerCallback: () => void,
    observerOptions: IntersectionObserverInit = { rootMargin: '100px' }
  ) => {
    // Prevent multiple observers
    if (observer.value) {
      return
    }

    if (!trigger.value) {
      // Retry in next tick if element is not ready, but only once
      nextTick(() => {
        if (!observer.value && trigger.value) {
          setupIntersectionObserver(trigger, observerCallback, observerOptions)
        }
      })
      return
    }

    observer.value = new IntersectionObserver(entries => {
      const [entry] = entries
      if (entry.isIntersecting) {
        observerCallback()
      }
    }, observerOptions)

    observer.value.observe(trigger.value)
  }

  /**
   * Cleanup intersection observer
   */
  const cleanupIntersectionObserver = () => {
    if (observer.value) {
      observer.value.disconnect()
      observer.value = undefined
    }
  }

  /**
   * Setup observer
   */
  const setupObserver = () => {
    if (!loadMoreTrigger.value || isObserverActive.value) {
      return
    }

    try {
      setupIntersectionObserver(loadMoreTrigger, debouncedCallback, { rootMargin, threshold })
      isObserverActive.value = true
    } catch (error) {
      console.warn('Failed to setup intersection observer:', error)
    }
  }

  /**
   * Cleanup observer
   */
  const cleanupObserver = () => {
    try {
      cleanupIntersectionObserver()
      isObserverActive.value = false

      if (timeoutRef.value) {
        clearTimeout(timeoutRef.value)
        timeoutRef.value = undefined
      }
    } catch (error) {
      console.warn('Failed to cleanup intersection observer:', error)
    }
  }

  /**
   * Setup observer when conditions are met
   */
  const setupObserverWhenReady = () => {
    if (enabled() && loadMoreTrigger.value && !isObserverActive.value) {
      nextTick(() => {
        // Double-check conditions after nextTick
        if (enabled() && loadMoreTrigger.value && !isObserverActive.value) {
          setupObserver()
        }
      })
    }
  }

  // Watch for changes in enabled state
  watch(
    () => enabled(),
    newEnabled => {
      if (newEnabled) {
        setupObserverWhenReady()
      } else {
        cleanupObserver()
      }
    },
    { immediate: true }
  )

  // Watch for when loadMoreTrigger is mounted or unmounted (e.g. v-if on the sentinel)
  watch(
    loadMoreTrigger,
    newTrigger => {
      if (!newTrigger) {
        if (isObserverActive.value) {
          cleanupObserver()
        }
        return
      }
      if (enabled() && !isObserverActive.value) {
        setupObserverWhenReady()
      }
    },
    { immediate: true }
  )

  // Cleanup on unmount
  onUnmounted(() => {
    cleanupObserver()
  })

  return {
    loadMoreTrigger,
    setupObserver: setupObserverWhenReady,
    cleanupObserver,
    isObserverActive,
  }
}
